/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/ban-types */
import stylesPP from './scss/FormPhoneFieldPP.module.scss';
import stylesSSO from './scss/FormPhoneFieldSSO.module.scss';
import PhoneInputWithCountry from 'react-phone-number-input/react-hook-form';
import { Country } from 'react-phone-number-input';
import { useController, useFormContext } from 'react-hook-form';
import { FormFieldProps } from '../FormField/FormField';
import 'react-phone-number-input/style.css';
import {
  hasValue,
  getProjectType,
  ProjectTypes,
  COUNTRY_MOBILE_PREFIX_IDENTIFIER,
  getDefaultCountryIdentifier,
  scrollToCenter,
} from '@r-and-a-shared-ui/utils';
import { defaultValidationMessage } from '../config/validators/validators';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getDefaultCountryCode,
  getCountryOptionsOrder,
  getDialCode,
  getFlags,
  getLabels,
} from '../../helpers/form-phone-field-helpers';
import FormPhoneFieldDropdown from './FormPhoneFieldDropdown';

export function FormPhoneField(props: FormFieldProps) {
  const {
    name,
    fieldRef,
    placeholder,
    label,
    subfields = [],
    isDisabled,
    validationMessage,
    options,
    hint,
  } = props;

  const {
    getValues,
    setValue,
    register,
    formState: { errors },
    control,
  } = useFormContext();

  const projectType = getProjectType();
  const isPlayerPortals = projectType === ProjectTypes.PlayerPortals;
  const styles = isPlayerPortals ? stylesPP : stylesSSO;
  const flagMenuPlacement = isPlayerPortals ? 'bottom' : 'top';

  const { field } = useController({ name, control });
  const { value, onChange, ...restField } = field;
  const formValues = getValues();

  const countryFieldIdentifier = getDefaultCountryIdentifier();
  const countryField =
    (subfields?.find(field =>
      field?.name.includes(countryFieldIdentifier),
    ) as FormFieldProps) || subfields?.at(0);
  const countryMobilePrefixField = subfields?.find(field =>
    field?.name.includes(COUNTRY_MOBILE_PREFIX_IDENTIFIER),
  ) as FormFieldProps;

  const defaultCountryCode = isPlayerPortals
    ? getDefaultCountryCode(
        countryField?.name ? getValues(countryField.name) : undefined,
        countryField?.options,
      )
    : getDefaultCountryCode(
        formValues[countryFieldIdentifier],
        countryField?.options,
      );
  const [fieldCountryCode, setFieldCountryCode] = useState<Country | undefined>(
    defaultCountryCode,
  );
  const [phoneInput, setPhoneInput] = useState(value);
  const [callingCode, setCallingCode] = useState(
    getDialCode(defaultCountryCode),
  );
  const isFirstRender = useRef(true);

  const isEmptyValuePhone =
    !phoneInput || phoneInput === '+' || phoneInput === callingCode;

  const setPhoneValue = useCallback(() => {
    if (isEmptyValuePhone) {
      onChange('');
      return;
    }

    onChange(phoneInput);
  }, [onChange, isEmptyValuePhone, phoneInput, defaultCountryCode]);

  const setCountryDialCode = useCallback(
    (phoneInput: string | undefined, callingCode: string) => {
      if (countryMobilePrefixField) {
        if (phoneInput && phoneInput !== '+' && phoneInput !== callingCode) {
          setValue(countryMobilePrefixField.name, callingCode);
        } else {
          setValue(countryMobilePrefixField.name, '');
        }
      }
    },
    [countryMobilePrefixField, setValue],
  );

  const handlePhoneInputChange = (value: string | undefined) => {
    setPhoneInput(value);
    if (!value) {
      onChange('');
    }
  };

  const handleCountryChange = (countryCode: Country | undefined) => {
    if (!countryCode) {
      return;
    }
    setFieldCountryCode(countryCode);
    const countryDialCode = getDialCode(countryCode);
    setCallingCode(countryDialCode);
  };

  useEffect(() => {
    if (!isPlayerPortals) {
      return;
    }
    let value = fieldCountryCode;
    if (isEmptyValuePhone) {
      value = undefined;
    }
    countryField && setValue(countryField?.name, value);
  }, [fieldCountryCode, isEmptyValuePhone, isPlayerPortals]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    setPhoneValue();
    setCountryDialCode(phoneInput, callingCode);
  }, [phoneInput, callingCode, setCountryDialCode, setPhoneValue]);

  return (
    <div
      className={`${styles['form-element__container']} ${
        errors?.[name] ? styles['form-element__has-error'] : ''
      }`}
      ref={fieldRef}
      onFocus={() => scrollToCenter(fieldRef)}
    >
      {hasValue(label) && (
        <label
          className={`${styles['form-element__label']} ${
            value ? styles['form-element__has-value'] : ''
          }`}
          htmlFor={name}
        >
          {label}
        </label>
      )}
      <PhoneInputWithCountry
        placeholder={placeholder}
        disabled={isDisabled}
        countryOptionsOrder={getCountryOptionsOrder(options)}
        value={value}
        defaultCountry={fieldCountryCode}
        {...register(name)}
        {...restField}
        onChange={handlePhoneInputChange}
        onCountryChange={handleCountryChange}
        international
        addInternationalOption={false}
        flags={getFlags(countryField)}
        labels={getLabels(countryField)}
        countrySelectComponent={FormPhoneFieldDropdown}
        countrySelectProps={{ ...props, menuPlacement: flagMenuPlacement }}
      />
      <div className={styles['form-element__bar']}></div>
      {errors?.[name] ? (
        <small className={styles['form-element__hint']}>
          {validationMessage || defaultValidationMessage}
        </small>
      ) : (
        hint ? (
          <small className={styles['form-element__hint']}>
            {hint}
          </small>
        ) : null
      )}
    </div>
  );
}

export default FormPhoneField;
